import { useParams, Link } from "react-router-dom";
import { useState, useEffect, useCallback, useContext } from "react";
import { db } from "../firebase/config";
import {
  collection,
  addDoc,
  getDocs,
  doc,
  getDoc,
  updateDoc
} from "firebase/firestore";
import * as XLSX from "xlsx";
import { auth } from "../firebase/config";
import { AuthContext } from "../AuthContext";
import { buttonStyles, inputStyles } from "../styles/ui";
import { GUEST_CLASSES, GUEST_STUDENTS } from "../utils/guestDummyData";

export default function ClassPage() {
  const { id } = useParams();
  const { isGuest } = useContext(AuthContext);

  const [students, setStudents] = useState([]);
  const [newStudentName, setNewStudentName] = useState("");
  const [className, setClassName] = useState("");
  const [classTeacherId, setClassTeacherId] = useState(null);
  const [editingStudent, setEditingStudent] = useState(null);
  const [editName, setEditName] = useState("");
  const [editTutorGroup, setEditTutorGroup] = useState("");
  const [editPupilPremiumStatus, setEditPupilPremiumStatus] = useState("");
  const [editSenStatus, setEditSenStatus] = useState("");
  const [editSenDetail, setEditSenDetail] = useState("");
  const [importFile, setImportFile] = useState(null);
  const [importStatus, setImportStatus] = useState("");
  const [importError, setImportError] = useState("");


  const canWriteToClass = useCallback(() => {
    if (isGuest) return false;
    
    const currentUser = auth.currentUser;
    const allowed = Boolean(currentUser?.uid && classTeacherId && currentUser.uid === classTeacherId);
    if (!allowed) {
      console.warn("Blocked write: not class owner", { classId: id, userId: currentUser?.uid });
    }
    return allowed;
  }, [classTeacherId, id, isGuest]);

  const startEditing = (student) => {
  setEditingStudent(student);
  setEditName(student.name);
  setEditTutorGroup(student.tutorGroup || "");
  setEditPupilPremiumStatus(student.pupilPremiumStatus || "");
  setEditSenStatus(student.senStatus || "");
  setEditSenDetail(student.senDetail || "");
};
const handleUpdateStudent = async (e) => {
  e.preventDefault();

  if (!canWriteToClass()) return;

  if (!editName.trim()) return;

  const studentRef = doc(db, "classes", id, "students", editingStudent.id);

  await updateDoc(studentRef, {
    name: editName,
    tutorGroup: editTutorGroup,
    pupilPremiumStatus: editPupilPremiumStatus,
    senStatus: editSenStatus,
    senDetail: editSenDetail
  });

  setEditingStudent(null);
  setEditName("");
  setEditTutorGroup("");
  setEditPupilPremiumStatus("");
  setEditSenStatus("");
  setEditSenDetail("");
  loadStudents();
};

  const handleArchiveStudent = async (student) => {
    const confirmed = window.confirm(
      `Archive ${student.name}? You can re-add later if needed.`
    );
    if (!confirmed) return;

    if (!canWriteToClass()) return;

    await updateDoc(doc(db, "classes", id, "students", student.id), {
      archived: true
    });

    if (editingStudent?.id === student.id) {
      setEditingStudent(null);
      setEditName("");
      setEditTutorGroup("");
      setEditPupilPremiumStatus("");
      setEditSenStatus("");
      setEditSenDetail("");
    }

    loadStudents();
  };



  const loadStudents = useCallback(async () => {
    if (isGuest) {
      // Load dummy students for guest
      const dummyStudents = GUEST_STUDENTS[id] || [];
      setStudents(dummyStudents.filter((student) => !student.archived));
      return;
    }

    const studentsRef = collection(db, "classes", id, "students");
    const snapshot = await getDocs(studentsRef);

    const studentList = snapshot.docs
      .map(doc => ({
        id: doc.id,
        ...doc.data()
      }))
      .filter((student) => !student.archived);

    setStudents(studentList);
  }, [id, isGuest]);

  const loadClassName = useCallback(async () => {
    if (isGuest) {
      // Load dummy class data for guest
      const dummyClass = GUEST_CLASSES.find((cls) => cls.id === id);
      if (dummyClass) {
        setClassName(dummyClass.name);
        setClassTeacherId(dummyClass.teacherId);
      }
      return;
    }

    const classRef = doc(db, "classes", id);
    const snapshot = await getDoc(classRef);

    if (snapshot.exists()) {
      const data = snapshot.data();
      setClassName(data?.name || "");
      setClassTeacherId(data?.teacherId || null);
    }
  }, [id, isGuest]);

  const handleAddStudent = async (e) => {
    e.preventDefault();

    if (!newStudentName.trim()) return;

    if (!canWriteToClass()) return;

    try {
      await addDoc(collection(db, "classes", id, "students"), {
        name: newStudentName,
        teacherId: auth.currentUser.uid,
        yearGroup: "",
        tutorGroup: "",
        pupilPremiumStatus: "",
        senStatus: "",
        senDetail: ""
      });

      setNewStudentName("");
      loadStudents();
    } catch (err) {
      console.error("Error adding student:", err);
    }
  };

  const normalizeHeader = (value) =>
    value.toLowerCase().replace(/[^a-z0-9]/g, "");

  const handleExcelImport = async () => {
    if (!importFile) return;
    setImportError("");
    setImportStatus("Importing...");

    if (!canWriteToClass()) {
      setImportStatus("");
      return;
    }

    try {
      const data = await importFile.arrayBuffer();
      const workbook = XLSX.read(data, { type: "array" });
      const sheetName = workbook.SheetNames[0];
      const sheet = workbook.Sheets[sheetName];
      const rows = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: "" });
      const cleanRows = rows.filter((row) => row.some((cell) => String(cell).trim() !== ""));

      if (cleanRows.length < 2) {
        setImportError("Excel file has no data rows.");
        setImportStatus("");
        return;
      }

      const requiredColumns = [
        { label: "Full Name", aliases: ["fullname"] },
        { label: "Year Group", aliases: ["yeargroup"] },
        { label: "Reg Group", aliases: ["reggroup", "registrationgroup", "registration", "reg"] },
        { label: "Pupil Premium Indicator", aliases: ["pupilpremiumindicator", "ppindicator", "pp"] },
        { label: "SEN Status", aliases: ["senstatus"] },
        { label: "SEN needs (All)", aliases: ["senneedsall", "senneeds", "sendetails", "sendetail"] },
      ];

      const headerRowIndex = cleanRows.findIndex((row) => {
        const normalized = row.map((value) => normalizeHeader(String(value)));
        return requiredColumns.every((col) =>
          normalized.some((header) => col.aliases.includes(header))
        );
      });

      if (headerRowIndex < 0) {
        setImportError("Missing required columns. Check the header row names.");
        setImportStatus("");
        return;
      }

      const headers = cleanRows[headerRowIndex].map((value) => normalizeHeader(String(value)));
      const getIndex = (aliases) =>
        headers.findIndex((header) => aliases.includes(header));

      const nameIndex = getIndex(requiredColumns[0].aliases);
      const yearGroupIndex = getIndex(requiredColumns[1].aliases);
      const regGroupIndex = getIndex(requiredColumns[2].aliases);
      const pupilPremiumIndex = getIndex(requiredColumns[3].aliases);
      const senStatusIndex = getIndex(requiredColumns[4].aliases);
      const senDetailIndex = getIndex(requiredColumns[5].aliases);

      const toValue = (row, index) =>
        index >= 0 && index < row.length ? String(row[index]).trim() : "";

      const formatImportedName = (value) => {
        const text = String(value || "").trim();
        if (!text) return "";
        if (text.includes(",")) {
          const parts = text.split(",").map((part) => part.trim()).filter(Boolean);
          if (parts.length >= 2) {
            return [parts[1], parts[0]].filter(Boolean).join(" ");
          }
        }
        return text;
      };

      const studentRows = cleanRows.slice(headerRowIndex + 1);
      let importedCount = 0;

      for (const row of studentRows) {
        const name = formatImportedName(toValue(row, nameIndex));

        if (!name) continue;

        await addDoc(collection(db, "classes", id, "students"), {
          name,
          teacherId: auth.currentUser.uid,
          yearGroup: toValue(row, yearGroupIndex),
          tutorGroup: toValue(row, regGroupIndex),
          pupilPremiumStatus: toValue(row, pupilPremiumIndex),
          senStatus: toValue(row, senStatusIndex),
          senDetail: toValue(row, senDetailIndex)
        });

        importedCount += 1;
      }

      setImportStatus(`Imported ${importedCount} students.`);
      setImportFile(null);
      await loadStudents();
    } catch (err) {
      console.error("Excel import failed:", err);
      setImportError("Import failed. Please check the Excel format.");
      setImportStatus("");
    }
  };

  useEffect(() => {
    loadClassName();
    loadStudents();
  }, [loadClassName, loadStudents]);

  return (
    <div>
      <h1 style={{ margin: "0 0 12px" }}>Class {className}</h1>
      {isGuest && (
        <div style={{ background: "#fff3cd", border: "1px solid #ffc107", padding: "12px", borderRadius: "4px", marginBottom: "16px", fontSize: "0.9rem" }}>
          📋 <strong>Demo Account:</strong> This is a read-only preview with sample data. You cannot edit or create classes.
        </div>
      )}
      <div style={{ display: "flex", gap: "10px", flexWrap: "wrap", marginBottom: "20px" }}>
        <Link
          to={`/classes/${id}/seating`}
          style={{ ...buttonStyles.secondary, display: "inline-block", textDecoration: "none" }}
        >
          Seating Plan
        </Link>
        <span
          style={{ ...buttonStyles.primary, display: "inline-block" }}
        >
          Classlist
        </span>
        <Link
          to={`/classes/${id}/seating?mode=teaching`}
          style={{ ...buttonStyles.secondary, display: "inline-block", textDecoration: "none" }}
        >
          Teaching Mode
        </Link>
        <Link
          to={`/classes/${id}/seating?objectives=1`}
          style={{ ...buttonStyles.secondary, display: "inline-block", textDecoration: "none" }}
        >
          Manage Objectives
        </Link>
        <Link
          to={`/class/${id}/analytics`}
          style={{ ...buttonStyles.secondary, display: "inline-block", textDecoration: "none" }}
        >
          Analytics
        </Link>
        <Link
          to="/classes"
          style={{ ...buttonStyles.secondary, display: "inline-block", textDecoration: "none" }}
        >
          Back to Classes
        </Link>
      </div>

      <h2>Add Student</h2>
      <form onSubmit={handleAddStudent}>
        <input
          type="text"
          placeholder="Student name"
          value={newStudentName}
          onChange={(e) => setNewStudentName(e.target.value)}
          style={inputStyles.input}
        />
        <button type="submit" style={buttonStyles.primary}>Add</button>
      </form>

      <h2 style={{ marginTop: "20px" }}>Import Students (Excel)</h2>
      <div style={{ display: "grid", gap: "8px", maxWidth: "520px" }}>
        <input
          type="file"
          accept=".xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
          onChange={(e) => {
            setImportFile(e.target.files?.[0] || null);
            setImportStatus("");
            setImportError("");
          }}
          style={inputStyles.input}
        />
        <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
          <button
            type="button"
            onClick={handleExcelImport}
            disabled={!importFile}
            style={
              importFile
                ? buttonStyles.secondary
                : { ...buttonStyles.secondary, ...buttonStyles.disabled }
            }
          >
            Import Excel
          </button>
          {importStatus && (
            <span style={{ color: "#2e7d32", fontSize: "0.9rem" }}>{importStatus}</span>
          )}
          {importError && (
            <span style={{ color: "#c62828", fontSize: "0.9rem" }}>{importError}</span>
          )}
        </div>
        <div style={{ fontSize: "0.85rem", color: "#666" }}>
          Required headers: Full Name, Year Group, Reg Group, Pupil Premium Indicator,
          SEN Status, SEN needs (All).
        </div>
      </div>
<h2>Students</h2>
      <ul
  style={{
    listStyle: "none",
    padding: 0,
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))",
    gap: "8px",
    maxWidth: "600px"
  
  }}
>
  {students.map((s) => (
    <li
      key={s.id}
      style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        padding: "6px 10px",
        background: "#f2f2f2",
        borderRadius: "6px"
      }}
    >
      <span>{s.name}</span>
      <div style={{ display: "flex", gap: "6px" }}>
        <button
          onClick={() => startEditing(s)}
          style={{ ...buttonStyles.secondary, fontSize: "0.8rem", padding: "0.35rem 0.6rem" }}
        >
          Edit
        </button>
        <button
          onClick={() => handleArchiveStudent(s)}
          style={{ ...buttonStyles.danger, fontSize: "0.8rem", padding: "0.35rem 0.6rem" }}
        >
          Archive
        </button>
      </div>
    </li>

  ))}
</ul>

{editingStudent && (
  <form onSubmit={handleUpdateStudent} style={{ marginTop: "20px" }}>
    <input
      type="text"
      value={editName}
      onChange={(e) => setEditName(e.target.value)}
      style={inputStyles.input}
    />
    <input
      type="text"
      placeholder="Tutor group"
      value={editTutorGroup}
      onChange={(e) => setEditTutorGroup(e.target.value)}
      style={inputStyles.input}
    />
    <input
      type="text"
      placeholder="Pupil premium status"
      value={editPupilPremiumStatus}
      onChange={(e) => setEditPupilPremiumStatus(e.target.value)}
      style={inputStyles.input}
    />
    <input
      type="text"
      placeholder="SEN status"
      value={editSenStatus}
      onChange={(e) => setEditSenStatus(e.target.value)}
      style={inputStyles.input}
    />
    <input
      type="text"
      placeholder="SEN detail"
      value={editSenDetail}
      onChange={(e) => setEditSenDetail(e.target.value)}
      style={inputStyles.input}
    />
    <button type="submit" style={buttonStyles.primary}>Save</button>
    <button type="button" onClick={() => setEditingStudent(null)} style={buttonStyles.secondary}>
      Cancel
    </button>
  </form>
)}
    </div>
  );
}